import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, startViewTransition } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
import { scrollPageToTop } from '@theme/dialog';
/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Search result type parameter (keeps product search + storefront filters working).
 * @type {string}
 */
const SEARCH_TYPE_PARAM = 'type';

/**
 * Search prefix-matching parameter used by the theme search forms.
 * @type {string}
 */
const SEARCH_PREFIX_PARAM = 'options[prefix]';

/**
 * Search-page params the facets form does not own and must not drop.
 * @type {string[]}
 */
const SEARCH_PRESERVE_KEYS = [
  SEARCH_QUERY,
  SEARCH_TYPE_PARAM,
  SEARCH_PREFIX_PARAM,
  'options[unavailable_products]',
  'options[fields]',
];

/**
 * Availability filter URL parameter (in-stock value).
 * @type {string}
 */
const AVAILABILITY_PARAM = 'filter.v.availability';

/**
 * In-stock value for the availability filter.
 * @type {string}
 */
const AVAILABILITY_IN_STOCK = '1';

/**
 * Ensures only one automatic-availability sync runs on initial load.
 * @type {boolean}
 */
let automaticAvailabilitySynced = false;

/**
 * Generation counter so only the latest in-flight filter render clears loading UI.
 * @type {number}
 */
let filterLoadingGeneration = 0;

/**
 * Class toggled on `document.documentElement` while a filter section render is in flight.
 * @type {string}
 */
const FACETS_FILTER_LOADING_CLASS = 'facets-filter-loading';

/**
 * Shows or hides filter-loading UI (drawer spinner overlay + disabled selections).
 * @param {boolean} isLoading
 */
function setFacetsFilterLoading(isLoading) {
  document.documentElement.classList.toggle(FACETS_FILTER_LOADING_CLASS, isLoading);

  const drawers = document.querySelectorAll('.facets--drawer');
  for (const drawer of drawers) {
    if (!(drawer instanceof HTMLElement)) continue;
    drawer.setAttribute('aria-busy', isLoading ? 'true' : 'false');

    const overlay = drawer.querySelector('.facets-drawer__loading-overlay');
    if (overlay instanceof HTMLElement) {
      overlay.setAttribute('aria-hidden', isLoading ? 'false' : 'true');
    }
  }

  const subcategories = document.querySelectorAll('.facets-dstrezzed-subcategory');
  for (const subcategory of subcategories) {
    if (!(subcategory instanceof HTMLElement)) continue;
    subcategory.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    subcategory.toggleAttribute('inert', isLoading);
  }
}

/**
 * @returns {boolean}
 */
function isFacetsFilterLoading() {
  return document.documentElement.classList.contains(FACETS_FILTER_LOADING_CLASS);
}

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  connectedCallback() {
    super.connectedCallback();
    this.#syncAutomaticAvailabilityOnLoad();
  }

  /**
   * Availability mode from `data-automatic-availability`.
   * @returns {'always' | 'when-filtered' | null}
   */
  #getAvailabilityMode() {
    const mode = this.getAttribute('data-automatic-availability');
    if (mode === 'always' || mode === 'when-filtered') return mode;
    return null;
  }

  /**
   * Returns true when any facet filter (excluding availability) is present.
   * Sort (`sort_by`) is intentionally ignored.
   * @param {URLSearchParams} params
   * @returns {boolean}
   */
  #hasActiveFacetFilters(params) {
    for (const key of params.keys()) {
      if (key.startsWith('filter.') && key !== AVAILABILITY_PARAM) {
        return true;
      }
    }

    return false;
  }

  /**
   * Applies in-stock availability based on mode:
   * - `always`: always set `filter.v.availability=1`
   * - `when-filtered`: set it only when another facet filter is active; remove otherwise
   * @param {URLSearchParams} params
   * @returns {URLSearchParams}
   */
  #applyAutomaticAvailability(params) {
    const mode = this.#getAvailabilityMode();
    if (!mode) return params;

    params.delete(AVAILABILITY_PARAM);

    if (mode === 'always' || this.#hasActiveFacetFilters(params)) {
      params.set(AVAILABILITY_PARAM, AVAILABILITY_IN_STOCK);
    }

    return params;
  }

  /**
   * Whether the current page is a search results page.
   * @returns {boolean}
   */
  #isSearchPage() {
    return /\/search\/?$/.test(window.location.pathname);
  }

  /**
   * Keeps search identity params (`q`, `type`, `options[prefix]`, …) when
   * filters or automatic availability rewrite the query string.
   * @param {URLSearchParams} params
   * @returns {URLSearchParams}
   */
  #preserveSearchParams(params) {
    const current = new URL(window.location.href).searchParams;

    for (const key of SEARCH_PRESERVE_KEYS) {
      if (params.has(key) || !current.has(key)) continue;

      for (const value of current.getAll(key)) {
        params.append(key, value);
      }
    }

    const searchQuery = this.#getSearchQuery() || params.get(SEARCH_QUERY);
    if (searchQuery) params.set(SEARCH_QUERY, searchQuery);

    return params;
  }

  /**
   * Search-results is a product grid. Keep Shopify search scoped to products
   * with last-prefix matching when filters rewrite the URL.
   * @param {URLSearchParams} params
   * @returns {URLSearchParams}
   */
  #ensureSearchDefaults(params) {
    if (!this.#isSearchPage()) return params;

    const searchQuery = params.get(SEARCH_QUERY) || this.#getSearchQuery();
    if (!searchQuery) return params;

    params.set(SEARCH_QUERY, searchQuery);
    if (!params.has(SEARCH_TYPE_PARAM)) params.set(SEARCH_TYPE_PARAM, 'product');
    if (!params.has(SEARCH_PREFIX_PARAM)) params.set(SEARCH_PREFIX_PARAM, 'last');

    return params;
  }

  /**
   * Puts search identity params first so `/search?q=…` stays a search URL
   * after filters and automatic availability are applied.
   * @param {URLSearchParams} params
   * @returns {URLSearchParams}
   */
  #orderParams(params) {
    const ordered = new URLSearchParams();

    for (const key of SEARCH_PRESERVE_KEYS) {
      for (const value of params.getAll(key)) {
        ordered.append(key, value);
      }
    }

    for (const [key, value] of params.entries()) {
      if (SEARCH_PRESERVE_KEYS.includes(key)) continue;
      ordered.append(key, value);
    }

    return ordered;
  }

  /**
   * On load, sync the URL when availability mode requires a different availability
   * param than the current URL (e.g. always-on missing, or when-filtered stale).
   */
  #syncAutomaticAvailabilityOnLoad() {
    if (!this.#getAvailabilityMode() || automaticAvailabilitySynced) return;

    automaticAvailabilitySynced = true;

    const url = new URL(window.location.href);
    const before = url.searchParams.toString();
    this.#preserveSearchParams(url.searchParams);
    this.#applyAutomaticAvailability(url.searchParams);

    if (url.searchParams.toString() === before) return;

    this.#ensureSearchDefaults(url.searchParams);
    const nextParams = this.#orderParams(url.searchParams);
    url.search = nextParams.toString();
    history.replaceState({ urlParameters: nextParams.toString() }, '', url.toString());
    this.dispatchEvent(new FilterUpdateEvent(nextParams));
    this.#updateSection();
  }

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    this.#preserveSearchParams(newParameters);
    this.#ensureSearchDefaults(newParameters);

    return this.#orderParams(this.#applyAutomaticAvailability(newParameters));
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    if (isFacetsFilterLoading()) return;

    scrollPageToTop();
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Updates the section
   */
  async #updateSection() {
    const generation = ++filterLoadingGeneration;
    setFacetsFilterLoading(true);

    const viewTransition = !this.closest('dialog');

    try {
      if (viewTransition) {
        /** @type {Promise<string> | undefined} */
        let renderPromise;
        await startViewTransition(() => {
          renderPromise = sectionRenderer.renderSection(this.sectionId);
          return renderPromise;
        }, ['product-grid']);
        // `startViewTransition` may resolve before an async callback finishes
        // when View Transitions are unsupported — still wait for the fetch/morph.
        if (renderPromise) await renderPromise;
      } else {
        await sectionRenderer.renderSection(this.sectionId);
      }
    } finally {
      if (generation === filterLoadingGeneration) {
        setFacetsFilterLoading(false);
      }
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    if (isFacetsFilterLoading()) return;

    scrollPageToTop();
    const nextUrl = new URL(url, window.location.href);
    this.#preserveSearchParams(nextUrl.searchParams);
    this.#ensureSearchDefaults(nextUrl.searchParams);
    this.#applyAutomaticAvailability(nextUrl.searchParams);
    nextUrl.search = this.#orderParams(nextUrl.searchParams).toString();
    history.pushState('', '', nextUrl);
    this.dispatchEvent(new FilterUpdateEvent(nextUrl.searchParams));
    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {string} */
  currency;
  /** @type {string} */
  moneyFormat;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.currency = this.dataset.currency ?? 'USD';
    this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat ?? '{{amount}}');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Extracts the placeholder from a money format string, removing currency symbols.
   * @param {string} format - The money format (e.g., "${{amount}}", "{{amount}} USD")
   * @returns {string} Just the placeholder (e.g., "{{amount}}")
   */
  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Parses a formatted money value into minor units
   * displayValue can come from user input or API response
   * @param {string} displayValue - The display value (e.g., "10.50" for USD, "9,50" for EUR, "1000" for JPY)
   * @param {string} currency - The currency code
   * @returns {number} The value in minor units
   */
  #parseDisplayValue(displayValue, currency) {
    return convertMoneyToMinorUnits(displayValue, currency) ?? 0;
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const { currency, moneyFormat } = this;
    // Parse the user's input value using currency-aware parsing
    const value = this.#parseDisplayValue(input.value, currency);

    // data-min and data-max now contain raw minor unit values (not formatted)
    const min = this.#parseDisplayValue(input.getAttribute('data-min') ?? '0', currency);
    const max = this.#parseDisplayValue(input.getAttribute('data-max') ?? '0', currency);

    if (value < min) {
      input.value = formatMoney(min, moneyFormat, currency);
    } else if (value > max) {
      input.value = formatMoney(max, moneyFormat, currency);
    }
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const activeClass = this.getAttribute('active-class') || 'active';
      clearButton.classList.toggle(activeClass, event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  /**
   * Accordion refs are only present for the dropdown sorting UI.
   * Dstrezzed drawer sorting is a flat radio list without details/summary/listbox.
   * @returns {string[]}
   */
  get requiredRefs() {
    if (this.classList.contains('sorting-filter--dstrezzed-overflow')) return [];
    return ['details', 'summary', 'listbox'];
  }

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';
    const selectElement = this.querySelector('select[name="sort_by"]');
    const radioInputs = this.querySelectorAll('input[name="sort_by"]');
    // Only swap select/radios when both exist. Dstrezzed drawer sorting is radios-only;
    // disabling radios there drops sort_by from FormData, and a missing select used to
    // early-return before updateFilters() ran.
    const hasSelectAndRadios =
      shouldDisable && selectElement instanceof HTMLSelectElement && radioInputs.length > 0;

    if (hasSelectAndRadios) {
      if (isMobile) {
        for (const input of radioInputs) {
          if (!(input instanceof HTMLInputElement)) continue;
          input.disabled = true;
        }
      } else {
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    if (hasSelectAndRadios) {
      if (isMobile) {
        for (const input of radioInputs) {
          if (!(input instanceof HTMLInputElement)) continue;
          input.disabled = false;
        }
      } else {
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected (dropdown UI only)
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;
    const shouldWrapInParentheses = facetStatus.dataset.summaryWrap === 'parentheses';

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    const summaryText = checkedInputElementsCount.toString();
    facetStatus.innerHTML = shouldWrapInParentheses ? `(${summaryText})` : summaryText;
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const currency = facetStatus.dataset.currency || '';
    const minInputNum = this.#parseCents(minInputValue, '0', currency);
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax, currency);
    const summaryText = `${this.#formatMoney(minInputNum)} – ${this.#formatMoney(maxInputNum)}`;
    const shouldWrapInParentheses = facetStatus.dataset.summaryWrap === 'parentheses';
    facetStatus.innerHTML = shouldWrapInParentheses ? `(${summaryText})` : summaryText;
  }

  /**
   * Parses a decimal number as minor units (cents for most currencies, but adjusted for zero-decimal currencies)
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid (formatted string like "11,400")
   * @param {string} currency - The currency code (e.g., 'USD', 'JPY', 'KRW')
   * @returns {number} The money value in minor units
   */
  #parseCents(value, fallback = '0', currency = '') {
    // Try to parse the value
    const result = convertMoneyToMinorUnits(value, currency);
    if (result !== null) return result;

    // Fall back to parsing the fallback string (which may have formatting like "11,400")
    const fallbackResult = convertMoneyToMinorUnits(fallback, currency);
    if (fallbackResult !== null) return fallbackResult;

    // Last resort: clean and parse as integer
    const cleanFallback = fallback.replace(/[^\d]/g, '');
    return parseInt(cleanFallback, 10) || 0;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const format = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return formatMoney(moneyValue, format, currency);
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Keeps Dstrezzed filter controls visible only while the product-grid
 * container is in view, and smoothly hides them near section boundaries.
 * Also sets `data-stuck` / `--stuck` when the sticky top bar is pinned,
 * and mirrors header fold-on-scroll when `data-fold-on-scroll` is present.
 */
function initDstrezzedToggleVisibility() {
  const MOBILE_BREAKPOINT = 750;
  const DESKTOP_HIDE_DISTANCE = 120;
  const STUCK_TOLERANCE_PX = 1;
  const SCROLL_DIRECTION_THRESHOLD = 12;
  let ticking = false;
  /** @type {number} */
  let lastScrollTop = document.scrollingElement?.scrollTop ?? 0;

  /**
   * Marks the sticky Dstrezzed controls bar when it is pinned under the header.
   * Requires scroll: on search the bar can already sit at CSS `top` before any
   * scroll (no breadcrumb offset), which would otherwise always look stuck.
   */
  const updateStuckState = () => {
    const controls = document.querySelector('.facets-dstrezzed-controls--full-width');

    if (!(controls instanceof HTMLElement)) return;

    const style = getComputedStyle(controls);
    const isStickyPosition = style.position === 'sticky' || style.position === 'fixed';
    // Use resting sticky offset (header), not the folded `top`, so pin detection stays stable while folded.
    const headerGroupHeight =
      Number.parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height')) || 0;
    const restingStickyTop = Math.max(0, headerGroupHeight - 1);
    const atPin = controls.getBoundingClientRect().top <= restingStickyTop + STUCK_TOLERANCE_PX;
    const hasScrolled = window.scrollY > STUCK_TOLERANCE_PX;
    const isFoldedAway =
      controls.hasAttribute('data-fold-on-scroll') && controls.dataset.scrollDirection === 'down';
    const isStuck = isStickyPosition && hasScrolled && (atPin || isFoldedAway);
    const nextValue = isStuck ? 'true' : 'false';

    if (controls.getAttribute('data-stuck') !== nextValue) {
      controls.setAttribute('data-stuck', nextValue);
    }

    controls.classList.toggle('facets-dstrezzed-controls--stuck', isStuck);
  };

  /**
   * Tracks scroll direction for fold-on-scroll (same idea as header.js):
   * hide while scrolling down once stuck; show again on scroll up / page top.
   */
  const updateFoldState = () => {
    const controls = document.querySelector('.facets-dstrezzed-controls--full-width');

    if (!(controls instanceof HTMLElement) || !controls.hasAttribute('data-fold-on-scroll')) return;

    const height = Math.round(controls.offsetHeight);

    if (height > 0) {
      controls.style.setProperty('--facets-dstrezzed-controls-height', `${height}px`);
    }

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const scrollDelta = scrollTop - lastScrollTop;
    const isStuck = controls.getAttribute('data-stuck') === 'true';
    /** @type {string} */
    let nextDirection = controls.dataset.scrollDirection || 'none';

    if (scrollTop <= 0) {
      nextDirection = 'none';
      lastScrollTop = 0;
    } else if (!isStuck) {
      nextDirection = 'none';
      lastScrollTop = scrollTop;
    } else if (Math.abs(scrollDelta) >= SCROLL_DIRECTION_THRESHOLD) {
      nextDirection = scrollDelta < 0 ? 'up' : 'down';
      lastScrollTop = scrollTop;
    }

    if (controls.dataset.scrollDirection !== nextDirection) {
      controls.dataset.scrollDirection = nextDirection;
    }
  };

  const syncMobileBarHeight = () => {
    const facetsToggle = document.querySelector('.facets-toggle--dstrezzed');
    if (!(facetsToggle instanceof HTMLElement)) return;

    if (window.innerWidth >= MOBILE_BREAKPOINT) {
      document.documentElement.style.removeProperty('--facets-dstrezzed-mobile-bar-height');
      return;
    }

    document.documentElement.style.setProperty(
      '--facets-dstrezzed-mobile-bar-height',
      `${facetsToggle.offsetHeight}px`
    );
  };

  const updateMobileVisibility = () => {
    const facetsToggle = document.querySelector('.facets-toggle--dstrezzed');
    if (!(facetsToggle instanceof HTMLElement)) return;

    syncMobileBarHeight();

    if (window.innerWidth >= MOBILE_BREAKPOINT) {
      facetsToggle.classList.remove('facets-toggle--hidden');
      return;
    }

    const container = facetsToggle.closest('.product-grid-container');
    if (!(container instanceof HTMLElement)) return;

    const rect = container.getBoundingClientRect();

    if (rect.bottom <= 0 || rect.top >= window.innerHeight) {
      facetsToggle.classList.add('facets-toggle--hidden');
      return;
    }

    facetsToggle.classList.remove('facets-toggle--hidden');
  };

  const updateDesktopVisibility = () => {
    const desktopControls = document.querySelector('.facets-dstrezzed-controls--full-width');
    if (!(desktopControls instanceof HTMLElement)) return;

    if (window.innerWidth < MOBILE_BREAKPOINT) {
      desktopControls.classList.remove('facets-dstrezzed-controls--hidden');
      desktopControls.style.opacity = '';
      desktopControls.style.transform = '';
      return;
    }

    const container = desktopControls.closest('.product-grid-container');
    if (!(container instanceof HTMLElement)) return;

    const rect = container.getBoundingClientRect();
    const distanceToViewportTop = rect.bottom;

    if (rect.bottom <= 0 || rect.top >= window.innerHeight) {
      desktopControls.classList.add('facets-dstrezzed-controls--hidden');
      return;
    }

    desktopControls.classList.remove('facets-dstrezzed-controls--hidden');

    if (distanceToViewportTop >= DESKTOP_HIDE_DISTANCE) {
      desktopControls.style.opacity = '1';
      desktopControls.style.transform = 'translateY(0)';
      return;
    }

    if (distanceToViewportTop <= 0) {
      desktopControls.style.opacity = '0';
      desktopControls.style.transform = 'translateY(-100%)';
      return;
    }

    const ratio = Math.max(0, Math.min(1, distanceToViewportTop / DESKTOP_HIDE_DISTANCE));
    const offset = (1 - ratio) * -100;
    desktopControls.style.opacity = ratio.toString();
    desktopControls.style.transform = `translateY(${offset}%)`;
  };

  const updateVisibility = () => {
    ticking = false;
    updateMobileVisibility();
    updateDesktopVisibility();
    updateStuckState();
    updateFoldState();
  };

  const requestUpdate = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(updateVisibility);
  };

  document.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
  document.addEventListener(ThemeEvents.FilterUpdate, requestUpdate);
  requestUpdate();
}

initDstrezzedToggleVisibility();

/**
 * Routes Dstrezzed subcategory links through section rendering and enables
 * grab-to-scroll (with momentum) when the hidden-scrollbar row overflows.
 * Toggles `.scrollable-left` / `.scrollable-right` for directional fade hints.
 */
function initDstrezzedSubcategoryNav() {
  const DRAG_THRESHOLD = 6;
  const FRICTION = 0.95;
  const MIN_VELOCITY = 0.05;
  const SCROLLABLE_LEFT_CLASS = 'scrollable-left';
  const SCROLLABLE_RIGHT_CLASS = 'scrollable-right';
  const NAV_SELECTOR = '.facets-dstrezzed-subcategory';
  const LIST_SELECTOR = '.facets-dstrezzed-subcategory__list';

  let scrollEl = null;
  let isPointerDown = false;
  let isDragging = false;
  let suppressClick = false;
  let startX = 0;
  let startScrollLeft = 0;
  let lastX = 0;
  let lastTime = 0;
  let velocity = 0;
  let momentumId = 0;
  let scrollableTicking = false;
  /** @type {MutationObserver | null} */
  let filterMorphObserver = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  let filterMorphTimeout = null;

  /**
   * @param {HTMLElement} container
   * @returns {boolean}
   */
  const hasHorizontalOverflow = (container) =>
    container.scrollWidth > container.clientWidth + 1;

  /**
   * @param {HTMLElement} container
   * @returns {{ left: boolean, right: boolean }}
   */
  const getScrollableEdges = (container) => {
    if (!hasHorizontalOverflow(container)) {
      return { left: false, right: false };
    }

    const maxScrollLeft = container.scrollWidth - container.clientWidth;

    return {
      left: container.scrollLeft > 1,
      right: container.scrollLeft < maxScrollLeft - 1,
    };
  };

  const updateScrollableClass = () => {
    const containers = document.querySelectorAll(NAV_SELECTOR);

    for (const container of containers) {
      if (!(container instanceof HTMLElement)) continue;

      const { left, right } = getScrollableEdges(container);
      container.classList.toggle(SCROLLABLE_LEFT_CLASS, left);
      container.classList.toggle(SCROLLABLE_RIGHT_CLASS, right);
    }
  };

  const requestScrollableUpdate = () => {
    if (scrollableTicking) return;

    scrollableTicking = true;
    requestAnimationFrame(() => {
      scrollableTicking = false;
      updateScrollableClass();
    });
  };

  const resizeObserver = new ResizeObserver(requestScrollableUpdate);

  const observeScrollableTargets = () => {
    const containers = document.querySelectorAll(NAV_SELECTOR);

    for (const container of containers) {
      resizeObserver.observe(container);

      const list = container.querySelector(LIST_SELECTOR);
      if (list) resizeObserver.observe(list);
    }
  };

  const stopMomentum = () => {
    if (!momentumId) return;
    cancelAnimationFrame(momentumId);
    momentumId = 0;
  };

  const startMomentum = () => {
    stopMomentum();

    const tick = () => {
      if (!scrollEl || Math.abs(velocity) < MIN_VELOCITY) {
        stopMomentum();
        requestScrollableUpdate();
        return;
      }

      scrollEl.scrollLeft -= velocity * 16;
      velocity *= FRICTION;
      requestScrollableUpdate();
      momentumId = requestAnimationFrame(tick);
    };

    momentumId = requestAnimationFrame(tick);
  };

  const cleanupPointerListeners = () => {
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', endDrag);
    document.removeEventListener('pointercancel', endDrag);
  };

  const endDrag = () => {
    if (!isPointerDown) return;

    const wasDragging = isDragging;

    isPointerDown = false;
    isDragging = false;
    scrollEl?.classList.remove('is-dragging');
    cleanupPointerListeners();
    requestScrollableUpdate();

    if (!wasDragging) return;

    startMomentum();
    // Suppress only the click that follows this drag gesture.
    suppressClick = true;
    setTimeout(() => {
      suppressClick = false;
    }, 0);
  };

  /**
   * @param {PointerEvent} event
   */
  const onPointerMove = (event) => {
    if (!isPointerDown || !scrollEl) return;

    const x = event.clientX;
    const now = performance.now();
    const deltaX = x - startX;

    if (!isDragging) {
      if (Math.abs(deltaX) <= DRAG_THRESHOLD) return;

      isDragging = true;
      scrollEl.classList.add('is-dragging');
    }

    const elapsed = Math.max(now - lastTime, 1);
    velocity = (x - lastX) / elapsed;
    lastX = x;
    lastTime = now;

    scrollEl.scrollLeft = startScrollLeft - deltaX;
    requestScrollableUpdate();
    event.preventDefault();
  };

  /**
   * FilterUpdate fires before section morph; wait for controls DOM changes
   * then re-observe and recheck overflow.
   */
  const handleFilterUpdate = () => {
    filterMorphObserver?.disconnect();
    if (filterMorphTimeout) clearTimeout(filterMorphTimeout);

    const controls = document.querySelector('.facets-dstrezzed-controls');
    if (!controls) {
      observeScrollableTargets();
      requestScrollableUpdate();
      return;
    }

    const settle = () => {
      filterMorphObserver?.disconnect();
      filterMorphObserver = null;
      if (filterMorphTimeout) {
        clearTimeout(filterMorphTimeout);
        filterMorphTimeout = null;
      }
      observeScrollableTargets();
      requestScrollableUpdate();
    };

    filterMorphObserver = new MutationObserver(() => {
      observeScrollableTargets();
      requestScrollableUpdate();
    });
    filterMorphObserver.observe(controls, { childList: true, subtree: true });
    // Morph is async (fetch + apply); stop watching after it should have settled.
    filterMorphTimeout = setTimeout(settle, 1500);
  };

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'touch') return;
    if (event.button !== 0) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const container = target.closest(NAV_SELECTOR);
    if (!(container instanceof HTMLElement)) return;
    // Allow drag while overflowed even at either end (so users can scroll back).
    if (!hasHorizontalOverflow(container)) return;

    stopMomentum();
    scrollEl = container;
    isPointerDown = true;
    isDragging = false;
    suppressClick = false;
    startX = event.clientX;
    startScrollLeft = container.scrollLeft;
    lastX = startX;
    lastTime = performance.now();
    velocity = 0;

    document.addEventListener('pointermove', onPointerMove, { passive: false });
    document.addEventListener('pointerup', endDrag);
    document.addEventListener('pointercancel', endDrag);
  });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('[data-dstrezzed-subcategory]');
    if (!(link instanceof HTMLAnchorElement)) return;

    if (suppressClick) {
      event.preventDefault();
      suppressClick = false;
      return;
    }

    // Collection-metafield subcategory links navigate between collections.
    if (link.getAttribute('data-dstrezzed-subcategory-nav') === 'collection') {
      return;
    }

    if (isFacetsFilterLoading()) {
      event.preventDefault();
      return;
    }

    const url = link.getAttribute('href');
    if (!url) return;

    const facetsForm = document.querySelector('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    event.preventDefault();
    facetsForm.updateFiltersByURL(url);
  });

  // Scroll does not bubble; capture so touch/native scroll still updates the fades.
  document.addEventListener(
    'scroll',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.matches(NAV_SELECTOR)) return;
      requestScrollableUpdate();
    },
    { capture: true, passive: true }
  );

  window.addEventListener('resize', requestScrollableUpdate, { passive: true });
  document.addEventListener(ThemeEvents.FilterUpdate, handleFilterUpdate);

  observeScrollableTargets();
  updateScrollableClass();
}

initDstrezzedSubcategoryNav();

/**
 * Syncs desktop + mobile Model/Product view toggles and mirrors the active
 * view onto `.collection-wrapper[product-card-view]`. Updates Model/Product
 * overlay + slideshow lead slides (Product mode keeps Model visible for hover
 * on desktop and as the second swipe slide on mobile; Model mode hides both
 * leads on desktop / Product lead on mobile), then resets each card slideshow
 * to the first visible slide.
 *
 * Preference is stored in sessionStorage so filter morph (which resets toggle
 * markup to Model) can restore Product after the section re-renders.
 */
function initDstrezzedProductCardViewToggle() {
  const TOGGLE_SELECTOR = '.facets-dstrezzed-controls__view-toggle';
  const WRAPPER_SELECTOR = '.collection-wrapper';
  const ATTR = 'product-card-view';
  const STORAGE_KEY = 'product-card-view';
  const VIEW_PRODUCT_CLASS = 'view-product';
  const DEFAULT_VIEW = 'model';
  const OVERLAY_IMAGE_SELECTOR = '.card-gallery__view-image[data-card-view]';
  const SLIDE_VIEW_SELECTOR = 'slideshow-slide[data-card-view]';
  const DESKTOP_VIEW_MQ = '(hover: hover) and (pointer: fine) and (min-width: 750px)';

  /** @type {MutationObserver | null} */
  let overlayObserver = null;
  /** @type {MutationObserver | null} */
  let filterMorphObserver = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  let filterMorphTimeout = null;
  let overlayObserverPaused = false;
  let overlaySyncTicking = false;

  /**
   * @returns {HTMLElement | null}
   */
  const getWrapper = () => {
    const wrapper = document.querySelector(WRAPPER_SELECTOR);
    return wrapper instanceof HTMLElement ? wrapper : null;
  };

  /**
   * @param {string | null | undefined} value
   * @returns {'model' | 'product'}
   */
  const normalizeView = (value) => (value === 'product' ? 'product' : DEFAULT_VIEW);

  /**
   * @returns {'model' | 'product' | null}
   */
  const getStoredView = () => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored === 'model' || stored === 'product') return stored;
    } catch {
      // sessionStorage may be unavailable
    }

    return null;
  };

  /**
   * @param {'model' | 'product'} view
   */
  const setStoredView = (view) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, view);
    } catch {
      // sessionStorage may be unavailable
    }
  };

  /**
   * Prefer sessionStorage, then the live wrapper attribute, then Model.
   * @returns {'model' | 'product'}
   */
  const getPreferredView = () => {
    const stored = getStoredView();
    if (stored) return stored;

    return normalizeView(getWrapper()?.getAttribute(ATTR));
  };

  /**
   * @returns {'model' | 'product'}
   */
  const getCurrentView = () => normalizeView(getWrapper()?.getAttribute(ATTR) ?? getStoredView());

  /**
   * @returns {boolean}
   */
  const isDesktopCardView = () => window.matchMedia(DESKTOP_VIEW_MQ).matches;

  /**
   * @param {ParentNode} [root]
   */
  const resetCardSlideshowsToFirst = (root = getWrapper() ?? document) => {
    const slideshows = root.querySelectorAll('.card-gallery--dstrezzed-product slideshow-component');

    for (const slideshow of slideshows) {
      if (!(slideshow instanceof HTMLElement) || typeof slideshow.select !== 'function') continue;
      if (slideshow.hasAttribute('data-dstrezzed-overlay-only')) continue;

      const slides = slideshow.slides;
      const firstVisible = Array.isArray(slides) ? slides[0] : null;
      if (!(firstVisible instanceof HTMLElement)) continue;

      const slideId = firstVisible.getAttribute('slide-id');
      if (!slideId) continue;

      slideshow.select({ id: slideId }, undefined, {
        animate: false,
        force: true,
      });
    }
  };

  /**
   * Sync overlay images + slideshow leads to the selected view.
   * Desktop Model mode: both slideshow leads stay hidden (overlay covers Model/Product).
   * Desktop Product mode: Product lead stays hidden; Model stays visible for hover.
   * Mobile Product mode: both leads stay visible (Product first, Model second).
   * Mobile Model mode: only the Model lead is shown; overlay is CSS-hidden.
   * Overlay-only cards keep leads hidden on every breakpoint (standalone overlay).
   * @param {'model' | 'product'} view
   * @param {ParentNode} [root]
   * @param {{ resetSlideshows?: boolean }} [options]
   */
  const applyCardViewOverlays = (view, root = getWrapper() ?? document, options = {}) => {
    const { resetSlideshows = false } = options;
    const isDesktop = isDesktopCardView();
    const images = root.querySelectorAll(OVERLAY_IMAGE_SELECTOR);
    const slides = root.querySelectorAll(SLIDE_VIEW_SELECTOR);

    overlayObserverPaused = true;

    for (const image of images) {
      if (!(image instanceof HTMLElement)) continue;

      const cardView = image.dataset.cardView;
      if (cardView !== 'model' && cardView !== 'product') continue;

      image.toggleAttribute('hidden', cardView !== view);
    }

    for (const slide of slides) {
      if (!(slide instanceof HTMLElement)) continue;

      const cardView = slide.dataset.cardView;
      if (cardView !== 'model' && cardView !== 'product') continue;

      const gallery = slide.closest('.card-gallery--dstrezzed-product');
      const overlayOnly = Boolean(
        gallery?.querySelector(':scope > .card-gallery__view-overlay--standalone') ||
          gallery?.querySelector('slideshow-component[data-dstrezzed-overlay-only]')
      );

      let hideLead = false;
      if (overlayOnly) {
        hideLead = true;
      } else if (isDesktop) {
        hideLead = view === 'product' ? cardView === 'product' : true;
      } else if (view === 'product') {
        hideLead = false;
      } else {
        hideLead = cardView !== 'model';
      }

      slide.toggleAttribute('hidden', hideLead);
      slide.removeAttribute('reveal');
    }

    if (resetSlideshows) {
      resetCardSlideshowsToFirst(root);
    }

    queueMicrotask(() => {
      overlayObserverPaused = false;
    });
  };

  /**
   * Sync desktop + mobile toggle chrome to the active view.
   * @param {'model' | 'product'} view
   */
  const applyToggleUi = (view) => {
    const toggles = document.querySelectorAll(TOGGLE_SELECTOR);

    for (const toggle of toggles) {
      if (!(toggle instanceof HTMLElement)) continue;

      const switchEl = toggle.querySelector('.facets-dstrezzed-controls__switch');
      const tabs = toggle.querySelectorAll('.facets-dstrezzed-controls__tab');

      if (switchEl instanceof HTMLElement) {
        switchEl.classList.toggle(VIEW_PRODUCT_CLASS, view === 'product');
        switchEl.setAttribute('aria-checked', view === 'product' ? 'true' : 'false');
      }

      for (const tab of tabs) {
        if (!(tab instanceof HTMLElement)) continue;

        const isActive = tab.dataset.productCardView === view;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      }
    }
  };

  /**
   * @param {'model' | 'product'} view
   * @param {{ resetSlideshows?: boolean }} [options]
   */
  const applyView = (view, options = {}) => {
    const { resetSlideshows = true } = options;
    const wrapper = getWrapper();
    if (!wrapper) return;

    setStoredView(view);
    wrapper.setAttribute(ATTR, view);
    applyToggleUi(view);
    applyCardViewOverlays(view, wrapper, { resetSlideshows });
  };

  const syncFromDom = () => {
    if (!document.querySelector(TOGGLE_SELECTOR)) return;
    if (!getWrapper()) return;

    applyView(getPreferredView());
  };

  /**
   * FilterUpdate fires before section morph; morph resets toggle markup to Model.
   * Capture the preferred view and re-apply after the controls DOM settles.
   */
  const handleFilterUpdate = () => {
    const preferredView = getPreferredView();
    let morphSyncTicking = false;

    filterMorphObserver?.disconnect();
    if (filterMorphTimeout) clearTimeout(filterMorphTimeout);

    const settle = () => {
      filterMorphObserver?.disconnect();
      filterMorphObserver = null;
      if (filterMorphTimeout) {
        clearTimeout(filterMorphTimeout);
        filterMorphTimeout = null;
      }
      applyView(preferredView, { resetSlideshows: true });
    };

    const root = getWrapper();
    if (!root) {
      settle();
      return;
    }

    filterMorphObserver = new MutationObserver(() => {
      if (morphSyncTicking) return;

      morphSyncTicking = true;
      requestAnimationFrame(() => {
        morphSyncTicking = false;
        applyView(preferredView, { resetSlideshows: false });
      });
    });
    filterMorphObserver.observe(root, { childList: true, subtree: true });
    // Morph is async (fetch + apply); stop watching after it should have settled.
    filterMorphTimeout = setTimeout(settle, 1500);
  };

  const observeOverlayMutations = () => {
    const wrapper = getWrapper();
    if (!wrapper || overlayObserver) return;

    overlayObserver = new MutationObserver(() => {
      if (overlayObserverPaused || overlaySyncTicking) return;
      if (!document.querySelector(TOGGLE_SELECTOR)) return;

      overlaySyncTicking = true;
      requestAnimationFrame(() => {
        overlaySyncTicking = false;
        const view = getPreferredView();
        applyToggleUi(view);
        applyCardViewOverlays(view, wrapper, { resetSlideshows: false });
      });
    });

    overlayObserver.observe(wrapper, {
      childList: true,
      subtree: true,
    });
  };

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const toggle = target.closest(TOGGLE_SELECTOR);
    if (!(toggle instanceof HTMLElement)) return;

    const tab = target.closest('.facets-dstrezzed-controls__tab');
    if (tab instanceof HTMLElement && toggle.contains(tab)) {
      const view = tab.dataset.productCardView;
      if (view === 'model' || view === 'product') {
        applyView(view);
      }
      return;
    }

    const switchEl = target.closest('.facets-dstrezzed-controls__switch');
    if (switchEl instanceof HTMLElement && toggle.contains(switchEl)) {
      applyView(getCurrentView() === 'model' ? 'product' : 'model');
    }
  });

  window.matchMedia(DESKTOP_VIEW_MQ).addEventListener('change', () => {
    if (!document.querySelector(TOGGLE_SELECTOR)) return;
    applyCardViewOverlays(getPreferredView(), getWrapper() ?? document, { resetSlideshows: true });
  });

  document.addEventListener(ThemeEvents.FilterUpdate, handleFilterUpdate);
  syncFromDom();
  observeOverlayMutations();
}

initDstrezzedProductCardViewToggle();
